import { useEffect, memo } from "react";
import { useStoreActions, useStoreState } from "easy-peasy";
import isEqual from "lodash.isequal";
import { sortQueuesByName } from "../../utils";
import * as Sentry from "@sentry/browser";
import { Device } from "@twilio/voice-sdk";
import { SyncClient } from "twilio-sync";

const TwilioServices = memo(() => {
  const supervisor = useStoreState(state => state.supervisor);
  const selectedDepartment = useStoreState(state => state.departments.selected);
  const setQueuesData = useStoreActions(actions => actions.queues.addQueues);
  const setAgentData = useStoreActions(actions => actions.agents.setData);
  const queuesLoading = useStoreState(state => state.queues.loading);
  const refreshValue = useStoreState(state => state.refresh.value);

  const twilioSync = () => {
    // initialize twilio sync client
    window.TwilioSync = new SyncClient(supervisor.twilioAccessToken, {
      logLevel: "silent" // silent, error, warn, info, debug, trace
    });
    let TwilioSync = window.TwilioSync;

    TwilioSync.on("connectionError", connectionError => {
      console.error(
        "[TWILIO-SYNC]: Client connection error",
        connectionError?.message
      );
      console.error(
        "[TWILIO-SYNC]:",
        "Is error terminal?",
        connectionError.terminal
      );

      if (connectionError?.terminal) {
        Sentry.captureMessage(
          "[TWILIO-SYNC]: Client failed to connect",
          "fatal"
        );
      }
    });

    TwilioSync.on("connectionStateChanged", state => {
      console.log("[TWILIO-SYNC]:", "Status Changed", state);
      if (state === "connected") {
        // If agentMap or queuesMap exists remove all listeners
        // to avoid duplicate events
        if (window.queuesMap) {
          window.queuesMap.close();
        }
        if (window.agentsMap) {
          window.agentsMap.close();
        }
        subscribeToQueues();
        subscribeToAgents();
      }
    });

    /* Token Events */
    TwilioSync.on("tokenAboutToExpire", () => {
      disableApplication("[TWILIO-SYNC]: Token about to expire");
    });

    TwilioSync.on("tokenExpired", () => {
      disableApplication("[TWILIO-SYNC]: Token expired");
    });
  };

  const subscribeToQueues = () => {
    const selectedDepartment = window.localStorage.getItem("departmentCode");

    let allQueues = {};

    // get all items from sync map
    window.TwilioSync.map(`${selectedDepartment}-queues`).then(function (map) {
      // store map globally
      window.queuesMap = map;
      // sync get items
      window.queuesMap.getItems({ pageSize: 999 }).then(page => {
        // console.log("Page items", page.items);

        page.items.forEach(queue => {
          if (
            parseInt(queue.data.department_id) ===
            parseInt(window.localStorage.getItem("departmentId"))
          ) {
            allQueues = { ...allQueues, [queue.key]: queue.data };
          }
        });

        setQueuesData({
          event: "allItemsAdded",
          items: sortQueuesByName(allQueues)
        });
      });

      window.queuesMap.on("itemAdded", data => {
        setQueuesData({
          event: "itemAdded",
          code: data.item.key,
          stats: data.item.data
        });
      });

      window.queuesMap.on("itemUpdated", data => {
        if (
          parseInt(data.item.data.department_id) ===
            parseInt(window.localStorage.getItem("departmentId")) &&
          !queuesLoading
        ) {
          if (!isEqual(data.item.data, data.previousItemData)) {
            setQueuesData({
              event: "itemUpdated",
              code: data.item.key,
              stats: data.item.data
            });
          }
        }
      });

      window.queuesMap.on("itemRemoved", data => {
        setQueuesData({
          event: "itemRemoved",
          code: data.key
        });
      });
    });
  };

  const subscribeToAgents = () => {
    console.log("Subscribing to agents...");
    // get all items from sync map
    window.TwilioSync.map(
      `${window.localStorage.getItem("departmentCode")}-agents`
    )
      .then(function (map) {
        // store map globally
        window.agentsMap = map;
        // sync get items
        map
          .getItems({ pageSize: 999 })
          .then(page => {
            let allItems = {};

            const processPage = page => {
              page.items.forEach(item => {
                if (
                  parseInt(item.data.departmentId) ===
                  parseInt(window.localStorage.getItem("departmentId"))
                ) {
                  allItems[item.key] = item.data;
                }
              });

              if (page.hasNextPage) {
                page.nextPage().then(processPage);
              }

              // If paging is finished loop through all items
              // and push into state
              if (!page.hasNextPage) {
                setAgentData({ event: "added", data: allItems });
              }
            };

            // Process initial page and begin paging
            processPage(page);
          })
          .catch(error => console.log("getItems() error", error));
        // sync item updated
        map.on("itemUpdated", data => {
          if (
            parseInt(data.item.data.departmentId) ===
            parseInt(window.localStorage.getItem("departmentId"))
          ) {
            // prettier-ignore
            setAgentData({ event: "itemUpdated", key: parseInt(data.item.key), data: data.item.data });
          }
        });
        // sync item added
        map.on("itemAdded", data => {
          if (
            parseInt(data.item.data.departmentId) ===
            parseInt(window.localStorage.getItem("departmentId"))
          ) {
            // prettier-ignore
            setAgentData({ event: "itemAdded", key: parseInt(data.item.key), data: data.item.data });
          }
        });
        // sync item removed
        map.on("itemRemoved", () => {
          // Items get removed from the map each night,
          // Once the items are removed we reload supervisor
          window.location.href = window.location.origin;
        });
        // sync map removed
        map.on("removed", () => {
          window.location.href = window.location.origin; // reload website
        });
      })
      .catch(errors => console.log("Could not subscribe to agents", errors));
  };

  useEffect(() => {
    window.queuesMap = null;
    window.agentsMap = null;
    // initialize Twilio Sync
    twilioSync();
    // if network connection is lost
    window.addEventListener("offline", () => {
      disableApplication();
    });
  }, []);

  useEffect(() => {
    if (refreshValue > 0) {
      console.log("[TWILIO-SYNC]: Data refresh triggered");
      if (window.queuesMap) window.queuesMap.close();
      if (window.agentsMap) window.agentsMap.close();
      subscribeToQueues();
      subscribeToAgents();
    }
  }, [refreshValue]);

  useEffect(() => {
    if (window.queuesMap) {
      window.queuesMap.close();
    }
    if (window.agentsMap) {
      window.agentsMap.close();
    }
    subscribeToQueues();
    subscribeToAgents();
  }, [selectedDepartment]);

  const disableApplication = message => {
    console.warn(message);
    // reload website
    window.location.href = window.location.origin;
  };

  return null;
});

export default TwilioServices;
